import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import ArrowLeftIcon from '@app/assets/icons/arrow-left.svg?react';
import { Button } from '@app/components/Button/Button';
import { AuditLogInterface } from '@app/models/log/audit';
import { getAuditLogActionColor } from '@app/constants/audit-log';
import { getAuditLogById } from '@app/services/admin';

export const AuditLogDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: auditLog,
    isLoading,
    isError,
  } = useQuery<AuditLogInterface>({
    queryKey: ['logs', 'audit', id],
    queryFn: () => getAuditLogById(id!),
    enabled: !!id,
  });

  const handleGoBack = () => {
    navigate('/admin/logs/audit');
  };

  const backButton = (
    <Button
      onClick={handleGoBack}
      kind="tertiary"
      className="flex items-center gap-2"
    >
      <ArrowLeftIcon className="w-4 h-4" />
      Back to Audit Logs
    </Button>
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">{backButton}</div>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded-md w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded-md w-1/2"></div>
          <div className="h-32 bg-gray-200 rounded-md"></div>
        </div>
      </div>
    );
  }

  if (isError || !auditLog) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">{backButton}</div>
        <div className="p-4 bg-red-50 text-red-700 rounded-md">
          Error loading audit log details. Please try again later.
        </div>
      </div>
    );
  }

  const renderMetadata = (metadata: any) => {
    if (!metadata || typeof metadata !== 'object') return null;

    return Object.entries(metadata).map(([key, value]) => (
      <div
        key={key}
        className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100"
      >
        <div>
          <p className="text-sm font-medium text-gray-700 capitalize">
            {key.replace(/([A-Z])/g, ' $1').trim()}
          </p>
        </div>
        <div className="col-span-2">
          <p className="text-gray-900 break-all">
            {typeof value === 'object'
              ? JSON.stringify(value, null, 2)
              : String(value)}
          </p>
        </div>
      </div>
    ));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">{backButton}</div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-gray-900">
                Audit Log Details
              </h1>
              <p className="text-sm text-gray-500">
                ID: {auditLog.id} •{' '}
                {format(new Date(auditLog.createdAt), 'PPpp')}
              </p>
            </div>
            <div className="flex flex-col gap-2 items-end">
              <span
                className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${getAuditLogActionColor(auditLog.action)}`}
              >
                {auditLog.action.replace('_', ' ')}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Admin Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Performed By (Admin)
            </h3>
            <div className="p-4 bg-blue-50 rounded-md">
              <div className="space-y-1">
                <p className="font-medium text-gray-900">
                  {auditLog.admin.firstName} {auditLog.admin.lastName}
                </p>
                <p className="text-sm text-gray-600">{auditLog.admin.email}</p>
                <p className="text-xs text-gray-500">ID: {auditLog.adminId}</p>
              </div>
            </div>
          </div>

          {/* Target User and Business Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {auditLog.user && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Target User
                </h3>
                <div className="p-4 bg-gray-50 rounded-md">
                  <div className="space-y-1">
                    <p className="font-medium text-gray-900">
                      {auditLog.user.firstName} {auditLog.user.lastName}
                    </p>
                    <p className="text-sm text-gray-600">
                      {auditLog.user.email}
                    </p>
                    <p className="text-xs text-gray-500">
                      ID: {auditLog.userId}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {auditLog.business && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Target Business
                </h3>
                <div className="p-4 bg-gray-50 rounded-md">
                  <div className="space-y-1">
                    <p className="font-medium text-gray-900">
                      {auditLog.business.name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {auditLog.business.email}
                    </p>
                    <p className="text-xs text-gray-500">
                      ID: {auditLog.businessId}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Metadata */}
          {auditLog.metadata && Object.keys(auditLog.metadata).length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Additional Information
              </h3>
              <div className="p-4 bg-gray-50 rounded-md">
                {renderMetadata(auditLog.metadata)}
              </div>
            </div>
          )}

          {/* Raw Metadata JSON */}
          {auditLog.metadata && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Raw Metadata
              </h3>
              <div className="p-4 bg-gray-900 rounded-md overflow-x-auto">
                <pre className="text-sm text-gray-100 font-mono whitespace-pre-wrap">
                  {JSON.stringify(auditLog.metadata, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLogDetail;
